/**
 * 订阅式实时通信原语：单 topic 订阅生命周期 + 防抖 → flush → 5s 轮询循环。
 *
 * 设计要点：
 * - 不感知业务语义（fullCode、字段集等都不在本类内部维护），仅关心「上行 subscribe payload 调度」与「下行解析派发」；
 * - 业务侧（如 SecChannelSubscriber / marketTipsChannel）通过传入的 `buildSubscribePayload` 工厂注入当前订阅 payload，
 *   订阅状态变更时由业务侧调用 {@link RealtimeSubscriber#requestFlush} 重新触发循环；
 * - 内部计时器槽位互斥：防抖窗口与轮询等待共用同一个 timer 引用。`requestFlush` 立即取消现有 timer，
 *   重新进入防抖窗口，从而满足「轮询期间若有新订阅则重置计时」的语义；
 * - 重连恢复继续依赖 `realtimeHub.subscribe` 的 `subscribePayload` 自动重发机制，本类透传同一个工厂给 hub。
 *
 * @module utils/realtime/RealtimeSubscriber
 */

import { isFunction, isNumber, isString } from 'lodash-es';
import { realtimeHub } from './index';
import { RealtimeStatus } from './constants';

/** 订阅变更合并发送的默认防抖间隔（毫秒） */
const DEFAULT_DEBOUNCE_MS = 200;

/** 订阅 payload 轮询重发的默认间隔（毫秒） */
const DEFAULT_POLL_INTERVAL_MS = 5000;

/**
 * 默认下行解析：若 data 是 JSON 字符串则尝试解析，失败 / 非字符串均按原值返回；
 * 进一步的业务结构归一化（如 stockDetail 内联 JSON）由各 channel 在 `onPush` 内自行处理。
 * @param {unknown} data
 * @returns {unknown}
 */
function parsePushDefault(data) {
  if (!isString(data)) {
    return data;
  }
  try {
    return JSON.parse(data);
  } catch (err) {
    console.warn('[RealtimeSubscriber] data JSON parse failed, fallback to raw value', err);
    return data;
  }
}

/**
 * @typedef {Object} RealtimeSubscriberOptions
 * @property {string} topic hub topic 与上行 payload `type` 同源；必填
 * @property {(data: unknown, raw: object) => void} onPush
 *   下行回调；接收的 `data` 已尝试 JSON.parse（失败时为原值），由业务自行做后续结构归一化；必填
 * @property {() => object | null | undefined} [buildSubscribePayload]
 *   上行 subscribe payload 工厂；返回 null/undefined 时跳过 send（用于纯下行场景）
 * @property {number} [debounceMs] 防抖窗口
 * @property {number} [pollIntervalMs] 轮询保活间隔
 */

export class RealtimeSubscriber {
  /**
   * @param {RealtimeSubscriberOptions} options
   */
  constructor(options) {
    const opts = options || {};
    if (!opts.topic || typeof opts.topic !== 'string') {
      throw new Error('[RealtimeSubscriber] topic is required');
    }
    if (!isFunction(opts.onPush)) {
      throw new Error('[RealtimeSubscriber] onPush is required');
    }
    this._topic = opts.topic;
    this._onPush = opts.onPush;
    this._buildSubscribePayload = isFunction(opts.buildSubscribePayload) ? opts.buildSubscribePayload : null;
    this._debounceMs = isNumber(opts.debounceMs) ? opts.debounceMs : DEFAULT_DEBOUNCE_MS;
    this._pollIntervalMs = isNumber(opts.pollIntervalMs) ? opts.pollIntervalMs : DEFAULT_POLL_INTERVAL_MS;

    /** @type {ReturnType<typeof setTimeout> | null} 当前未触发的 flush timer（防抖或轮询共用一个槽位） */
    this._flushTimer = null;
    /** @type {(() => void) | null} hub 上 topic 订阅的解绑句柄 */
    this._hubUnsub = null;
    this._installed = false;
  }

  /**
   * 是否已安装；外部业务在「首次激活订阅」时可据此判断是否需要再次 install。
   * @returns {boolean}
   */
  isInstalled() {
    return this._installed;
  }

  /**
   * 幂等安装：建立 hub 连接 + 注册 topic 订阅 + 立即调度首次 flush。
   * 重连恢复仍由 hub 的 `subscribePayload` 重发机制兜底。
   * @returns {void}
   */
  install() {
    if (this._installed) {
      return;
    }
    this._installed = true;
    // 页面重新激活订阅属于明确的用户活跃信号：若 hub 因服务端断连超限处于 FAILED 锁定态，
    // 先 disconnect() 解锁（清零断连计数、保留订阅表）再重连，避免用户只能刷新页面恢复
    if (realtimeHub.status === RealtimeStatus.FAILED) {
      realtimeHub.disconnect();
    }
    realtimeHub.connect();
    // 仅在配置了 buildSubscribePayload 时才透传给 hub 的重连重发钩子；纯下行场景下不带该字段
    const subOpts = this._buildSubscribePayload ? { subscribePayload: this._buildSubscribePayload } : {};
    this._hubUnsub = realtimeHub.subscribe(this._topic, (data, raw) => this._handlePush(data, raw), subOpts);
    // 首次安装立即调度一次 flush，让 hub 连接打通后第一时间发出当前 subscribe payload
    this._scheduleFlush();
  }

  /**
   * 幂等销毁：清掉未触发的 timer 并解绑 hub 订阅。
   * @returns {void}
   */
  destroy() {
    if (!this._installed) {
      return;
    }
    this._installed = false;
    this._cancelTimer();
    if (this._hubUnsub) {
      try {
        this._hubUnsub();
      } catch (e) {
        console.error('[RealtimeSubscriber] hub unsubscribe failed', e);
      }
      this._hubUnsub = null;
    }
  }

  /**
   * 业务侧通知「订阅状态可能已变」：取消现有 timer 并进入新的防抖窗口；
   * 防抖结束后真正 flush，并接力安排 5s 轮询。期间再次调用会立即重置计时。
   *
   * 未 install 时调用 no-op，避免业务侧在 destroy 后误调用导致悬挂 timer。
   * @returns {void}
   */
  requestFlush() {
    if (!this._installed) {
      return;
    }
    this._scheduleFlush();
  }

  /**
   * 防抖：在 `debounceMs` 内合并多次 requestFlush；触发后 flush + 接力轮询。
   * @returns {void}
   */
  _scheduleFlush() {
    this._cancelTimer();
    this._flushTimer = setTimeout(() => {
      this._flushTimer = null;
      this._flush();
      // 真正发出 payload 后进入 5s 轮询节奏继续维持
      this._schedulePoll();
    }, this._debounceMs);
  }

  /**
   * 5s 后再次重发当前 subscribe payload，作为轮询保活；纯下行场景下仍空转，
   * 业务侧若 destroy 会通过 `_installed=false` + `_cancelTimer` 自然退出循环。
   * @returns {void}
   */
  _schedulePoll() {
    if (!this._installed) {
      return;
    }
    this._flushTimer = setTimeout(() => {
      this._flushTimer = null;
      if (!this._installed) {
        return;
      }
      this._flush();
      this._schedulePoll();
    }, this._pollIntervalMs);
  }

  /**
   * 立即调用 buildSubscribePayload 并 send；纯下行场景（无 builder 或 builder 返回空）跳过 send。
   * @returns {void}
   */
  _flush() {
    if (!this._buildSubscribePayload) {
      return;
    }
    const payload = this._buildSubscribePayload();
    if (!payload) {
      return;
    }
    realtimeHub.send(payload);
  }

  /**
   * 取消当前未触发的 flush timer（防抖或轮询）。
   * @returns {void}
   */
  _cancelTimer() {
    if (!this._flushTimer) {
      return;
    }
    clearTimeout(this._flushTimer);
    this._flushTimer = null;
  }

  /**
   * 处理下行：默认对 data 做一次 JSON.parse 兜底（失败则返回原值），再透传给 onPush。
   * @param {unknown} data 下行消息的 data 段
   * @param {object} raw 原始消息（透传给上层做诊断）
   */
  _handlePush(data, raw) {
    const parsed = parsePushDefault(data);
    this._onPush(parsed, raw);
  }
}
